import { useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { X, FileText, Stethoscope, AlertTriangle, ClipboardList } from 'lucide-react';

/**
 * DemoSummaryModal — Loom-recording-only side-by-side view.
 * Renders a mock patient case (Eleanor Hartwell, 68, T2DM + HTN + HLD + ankle injury)
 * showing the Patient 2-page Summary on the LEFT and the Physician SOAP on the RIGHT.
 * No backend calls. Mock data hard-coded. Trigger: Header button (demo-mode only).
 */
export default function DemoSummaryModal({ open, onClose }) {
  // Esc-to-close convenience (Dialog handles this too; safety net for Loom recording)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="max-w-[1400px] w-[96vw] h-[92vh] p-0 overflow-hidden border-2 border-slate-300"
        data-testid="demo-summary-modal"
      >
        {/* Top bar */}
        <div className="flex items-center justify-between bg-slate-900 text-white px-5 py-3">
          <div className="flex items-center gap-3">
            <div className="text-xs uppercase tracking-widest text-emerald-300 font-bold">Demo Mode · Mock Case</div>
            <div className="text-sm font-semibold">Eleanor Hartwell · 68 F · United States · Encounter <span className="font-mono text-emerald-300">PAI-2026-DEMO-A1</span></div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-red-300 transition"
            aria-label="Close demo view"
            data-testid="demo-summary-close-btn"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Two-column body */}
        <div className="grid grid-cols-2 gap-0 overflow-hidden" style={{ height: 'calc(92vh - 56px)' }}>

          {/* LEFT — Patient 2-page summary */}
          <div className="overflow-y-auto bg-blue-50/30 border-r-2 border-slate-300 p-6" data-testid="demo-patient-summary">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-5 h-5 text-[#2563EB]" />
              <h2 className="text-lg font-bold text-slate-900">Patient Summary</h2>
              <span className="text-[10px] uppercase tracking-wider bg-[#2563EB] text-white px-2 py-0.5 rounded font-bold">What the patient sees</span>
            </div>
            <p className="text-xs italic text-slate-500 mb-5">Two pages. Plain language. No clinical jargon. Available in the patient portal and via the 24/7 Virtual Assistant.</p>

            {/* Page 1 */}
            <div className="bg-white border border-slate-200 rounded-md p-5 mb-4 shadow-sm">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-3">Page 1 — Your visit, in your words</div>

              <h3 className="text-sm font-bold text-slate-900 mt-2 mb-1">Why you came in today</h3>
              <p className="text-sm text-slate-700 leading-relaxed">"My left ankle has been hurting for about ten days, since I slipped on the back step. It's swollen and turns purple at night. I'm worried because I'm diabetic and my mother lost a leg to diabetes. I haven't been able to do my morning walks and my sugar readings are creeping up."</p>

              <h3 className="text-sm font-bold text-slate-900 mt-4 mb-1">Your active health conditions</h3>
              <ul className="text-sm text-slate-700 list-disc ml-5 space-y-0.5">
                <li>Type 2 Diabetes — since 2017</li>
                <li>High blood pressure — since 2015</li>
                <li>High cholesterol — since 2019</li>
                <li>Left ankle injury — since May 16, 2026</li>
                <li>Penicillin allergy — lifelong (hives)</li>
              </ul>

              <h3 className="text-sm font-bold text-slate-900 mt-4 mb-1">Your past health events</h3>
              <ul className="text-sm text-slate-700 list-disc ml-5 space-y-0.5">
                <li>Cholecystectomy — 2008 (gallbladder removed)</li>
                <li>Cesarean section — 1984</li>
                <li>Right shoulder rotator-cuff repair — 2019</li>
              </ul>

              <h3 className="text-sm font-bold text-slate-900 mt-4 mb-1">Your medications</h3>
              <ul className="text-sm text-slate-700 list-disc ml-5 space-y-0.5">
                <li>Metformin 1000 mg — twice daily</li>
                <li>Lisinopril 20 mg — once daily</li>
                <li>Atorvastatin 40 mg — at bedtime</li>
                <li>Aspirin 81 mg — once daily</li>
                <li>Tylenol 500 mg — as needed for ankle pain</li>
                <li>Glucosamine + turmeric — OTC, started 3 weeks ago</li>
              </ul>

              <h3 className="text-sm font-bold text-slate-900 mt-4 mb-1">Family history (relevant)</h3>
              <p className="text-sm text-slate-700">Mother — diabetes, below-knee amputation. Father — heart attack at 62. One sister — high cholesterol.</p>
            </div>

            {/* Page 2 */}
            <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-3">Page 2 — Questions your doctor is likely to ask</div>
              <p className="text-xs text-slate-500 italic mb-3">Reviewing these in advance helps you give a clearer answer during the 15-minute visit.</p>

              <ol className="text-sm text-slate-700 list-decimal ml-5 space-y-2">
                <li>Did you fall, or did you twist the ankle as you slipped? <em className="text-slate-500">— mechanism matters for fracture risk.</em></li>
                <li>Can you put any weight on the foot when you stand up?</li>
                <li>Have you noticed any numbness, tingling, or color change in the toes?</li>
                <li>Have your home blood-sugar readings been higher than usual since the injury? By how much?</li>
                <li>Are you taking any new pain or supplement products you bought without a prescription?</li>
                <li>Have you had any new chest pain, shortness of breath, or palpitations since you reduced activity?</li>
                <li>When was your last eye exam, and your last foot exam by the diabetes nurse?</li>
                <li>How are you sleeping? Any changes in mood since being unable to walk?</li>
              </ol>

              <div className="mt-5 border-t border-slate-200 pt-3">
                <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold mb-1">Bring with you to the visit</div>
                <ul className="text-sm text-slate-700 list-disc ml-5 space-y-0.5">
                  <li>Your home blood-sugar log (last 14 days).</li>
                  <li>Your home blood-pressure log if you have one.</li>
                  <li>The bottle of the glucosamine + turmeric supplement (we'll check the label).</li>
                </ul>
              </div>
            </div>

            <p className="text-[10px] text-slate-400 italic mt-4">This summary is what the patient sees. The full SOAP note is for the physician, accessible only through the secure clinical view on the right.</p>
          </div>

          {/* RIGHT — Physician SOAP note */}
          <div className="overflow-y-auto bg-slate-50 p-6" data-testid="demo-physician-soap">
            <div className="flex items-center gap-2 mb-3">
              <Stethoscope className="w-5 h-5 text-[#EF4444]" />
              <h2 className="text-lg font-bold text-slate-900">Physician SOAP Note</h2>
              <span className="text-[10px] uppercase tracking-wider bg-[#EF4444] text-white px-2 py-0.5 rounded font-bold">What the doctor signs</span>
            </div>
            <p className="text-xs italic text-slate-500 mb-5">Structured SOAP. Auditable, rule by rule. Physician edits and signs — P.Ai is not an AI doctor.</p>

            {/* Risk flags strip */}
            <div className="bg-white border-2 border-amber-400 rounded-md p-4 mb-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span className="text-xs uppercase tracking-widest text-amber-800 font-bold">Deterministic risk flags · 3 fired</span>
              </div>
              <ul className="text-xs text-slate-800 space-y-1.5">
                <li><strong className="text-red-700">[CRITICAL]</strong> Diabetic foot trauma pathway — Rule <code className="bg-slate-100 px-1 rounded">ENDO-DM-FOOT-007</code> · ADA 2024 §11.6</li>
                <li><strong className="text-amber-700">[MODERATE]</strong> Anti-platelet + NSAID-adjacent bleed risk — Rule <code className="bg-slate-100 px-1 rounded">PHARM-BLEED-012</code> · Aspirin 81 mg + recent OTC supplements (turmeric ↑ bleeding risk)</li>
                <li><strong className="text-amber-700">[MODERATE]</strong> Glycemic destabilization on reduced activity — Rule <code className="bg-slate-100 px-1 rounded">ENDO-DM-GLY-021</code> · Self-reported home readings ↑ post-injury</li>
              </ul>
            </div>

            {/* SOAP — Subjective */}
            <div className="bg-white border border-slate-200 rounded-md p-5 mb-3 shadow-sm">
              <div className="text-[10px] uppercase tracking-widest text-[#2563EB] font-bold mb-2">S — Subjective</div>
              <p className="text-sm text-slate-800 leading-relaxed"><strong>CC:</strong> Left ankle pain × 10 days, post-mechanical (slip), with anxiety re: diabetic limb history.</p>
              <p className="text-sm text-slate-800 leading-relaxed mt-2"><strong>HPI:</strong> 68F with T2DM (HbA1c 7.8% prior visit), HTN, HLD presents 10 days after slipping on rear-step concrete. Twisting mechanism — no fall to ground. Persistent swelling, evening discoloration, partial weight-bearing tolerable. Denies numbness, tingling. Home fingersticks reportedly trending 180–230 mg/dL fasting since injury (baseline 110–140). Initiated OTC glucosamine + turmeric ~3 wks prior to injury for "joint health."</p>
              <p className="text-sm text-slate-800 leading-relaxed mt-2"><strong>ROS:</strong> Negative for chest pain, dyspnea, palpitations, fever, paresthesia, vision changes. Sleep mildly disrupted by ankle pain.</p>
            </div>

            {/* SOAP — Objective */}
            <div className="bg-white border border-slate-200 rounded-md p-5 mb-3 shadow-sm">
              <div className="text-[10px] uppercase tracking-widest text-[#2563EB] font-bold mb-2">O — Objective</div>
              <p className="text-sm text-slate-800"><strong>Vitals:</strong> BP 148/86 · HR 78 · RR 16 · SpO₂ 98% · BMI 31.4 · Random glucose 198 mg/dL.</p>
              <p className="text-sm text-slate-800 mt-2"><strong>Exam (left ankle):</strong> Lateral malleolar swelling, no point tenderness over distal fibula or talar dome. Ecchymosis evolving. Range of motion limited by pain; passive inversion mildly painful. Weight-bearing 4 steps in office without collapse. Distal pulses 2+ symmetric. Sensation intact to monofilament 10g at 6/6 forefoot sites bilaterally.</p>
              <p className="text-sm text-slate-800 mt-2"><strong>Skin (diabetic foot exam):</strong> No callus, no ulceration, no maceration. Capillary refill &lt;3s. Nails atraumatic.</p>
            </div>

            {/* SOAP — Assessment */}
            <div className="bg-white border border-slate-200 rounded-md p-5 mb-3 shadow-sm">
              <div className="text-[10px] uppercase tracking-widest text-[#2563EB] font-bold mb-2">A — Assessment <span className="text-slate-400 font-normal">(ranked by acuity)</span></div>
              <ol className="text-sm text-slate-800 list-decimal ml-5 space-y-1.5">
                <li><strong>Left lateral ankle sprain</strong>, Grade II likely — Ottawa Ankle Rules negative for high-probability fracture; imaging discretionary per shared decision.</li>
                <li><strong>Type 2 Diabetes — destabilized</strong>, post-injury hypoactivity.</li>
                <li><strong>Hypertension — uncontrolled at today's visit</strong> (148/86).</li>
                <li><strong>Polypharmacy bleed risk</strong> — aspirin + turmeric supplement; counsel on NSAID avoidance.</li>
                <li><strong>Patient anxiety — diabetic limb-loss family history</strong>; address explicitly.</li>
              </ol>
            </div>

            {/* SOAP — Plan */}
            <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm">
              <div className="text-[10px] uppercase tracking-widest text-[#2563EB] font-bold mb-2">P — Plan</div>
              <ul className="text-sm text-slate-800 list-disc ml-5 space-y-1.5">
                <li><strong>Ankle:</strong> RICE protocol, lace-up brace, acetaminophen 1g q6h PRN. <em>Avoid NSAIDs</em> (renal + bleed risk on aspirin + turmeric). Return in 7 days; X-ray if not improving.</li>
                <li><strong>Diabetes:</strong> Continue metformin 1g BID. Increase home-monitoring frequency to QID for 14 days. Repeat HbA1c at 6-week follow-up. If fasting persists &gt;160, initiate SGLT-2 inhibitor (empagliflozin 10 mg) — review ADA 2024 first-line CV-protective class.</li>
                <li><strong>HTN:</strong> Lisinopril 20 → 30 mg daily. Home BP log × 2 weeks; goal &lt;130/80.</li>
                <li><strong>Supplements:</strong> Discontinue turmeric while on aspirin. Glucosamine acceptable.</li>
                <li><strong>Counseling:</strong> Address mother-amputation anxiety. Diabetic foot self-exam handout. Refer to diabetes-educator group session (next Thursday).</li>
                <li><strong>Follow-up:</strong> 7 days (ankle re-eval) · 6 weeks (diabetes + HTN re-eval with labs).</li>
              </ul>
            </div>

            <div className="flex items-center gap-2 mt-5 pt-3 border-t border-slate-200">
              <ClipboardList className="w-4 h-4 text-slate-500" />
              <p className="text-[10px] text-slate-500 italic">Physician signature required to commit. All flags traceable to deterministic Python rules; no LLM in the diagnostic loop.</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
